import Country from '../Shipping/Country';
import State from '../Shipping/State';
import { nullSafeToString } from '../lib/util';

/**
 * A new address being entered during checkout.
 *
 * The country and state fields cascade: assigning a {@link CheckoutAddress#country country} clears any
 * state that was already chosen, empties the stale {@link CheckoutAddress#states states} list, and
 * drops the postal code for countries that do not use one. So you never have to clear those yourself -
 * assign the country and then call
 * {@link Collaterate.Checkout.refreshStates Collaterate.Checkout.refreshStates} to load the new list.
 *
 * Create one with {@link Collaterate.Checkout.createAddressForm Collaterate.Checkout.createAddressForm}
 * rather than directly, then hand it to
 * {@link Collaterate.Checkout.addAddress Collaterate.Checkout.addAddress} to save it.
 *
 * @property {Country[]} countries - (Read only) The countries this project can ship to.
 * @property {State[]} states - (Read only) The states for the selected country. Empty until {@link Collaterate.Checkout.refreshStates refreshStates} has run, and emptied again whenever the country changes.
 * @property {?Country} country - The selected country. Must be one of {@link CheckoutAddress#countries countries}.
 * @property {?State} state - The selected state. Must be one of {@link CheckoutAddress#states states}.
 * @property {string} postalCode - The postal code. Cleared automatically when the selected country does not use postal codes.
 * @property {string} firstName - First name of the contact at this address.
 * @property {string} lastName - Last name of the contact at this address.
 * @property {string} company - Company at this address.
 * @property {string} phone - Contact phone number.
 * @property {string} address - Street address.
 * @property {string} address2 - Second street address line.
 * @property {string} city - City.
 * @property {string} addressType - The address type: "SHIP_TO", "BILL_TO" or "BLIND_FROM". Decides which address book the saved address joins.
 */

class CheckoutAddress {
    constructor (data = {}) {
        const countries = Object.freeze(data.countries || []);

        let country = null;
        let state = null;
        let states = Object.freeze([]);
        let postalCode = nullSafeToString(data.postalCode);

        Object.defineProperties(this, {
            // Not enumerable: an internal handle for refreshStates, the same way ShippingEstimate
            // carries the project it belongs to.
            projectId: {
                value: data.projectId,
            },
            countries: {
                value: countries,
                enumerable: true,
            },
            states: {
                get: () => states,
                set: newStates => {
                    states = Object.freeze(newStates || []);

                    // A state that is no longer offered cannot stay selected.
                    if (state && !states.find(candidate => candidate.id === state.id)) {
                        state = null;
                    }

                    return states;
                },
                enumerable: true,
            },
            country: {
                get: () => country,
                set: function (selectedCountry) {
                    if (!selectedCountry) {
                        state = null;
                        states = Object.freeze([]);
                        return country = null;
                    }

                    if (selectedCountry.constructor !== Country) {
                        console.warn('CheckoutAddress#country must be a Country. Country not set.');
                        return;
                    }

                    if (!this.countries.find(candidate => candidate.id === selectedCountry.id)) {
                        console.warn('CheckoutAddress#country must be one of CheckoutAddress#countries. Country not set.');
                        return;
                    }

                    // Reassigning the same country is a no-op, so an in-progress state survives it.
                    if (country && country.id === selectedCountry.id) {
                        return country;
                    }

                    state = null;
                    // The list belongs to the old country; refreshStates repopulates it.
                    states = Object.freeze([]);

                    if (!selectedCountry.hasPostalCodes) {
                        postalCode = '';
                    }

                    return country = selectedCountry;
                },
                enumerable: true,
            },
            state: {
                get: () => state,
                set: function (selectedState) {
                    if (!selectedState) {
                        return state = null;
                    }

                    if (selectedState.constructor !== State) {
                        console.warn('CheckoutAddress#state must be a State. State not set.');
                        return;
                    }

                    if (!this.states.find(candidate => candidate.id === selectedState.id)) {
                        console.warn('CheckoutAddress#state must be one of CheckoutAddress#states. Call Collaterate.Checkout.refreshStates() after changing the country. State not set.');
                        return;
                    }

                    return state = selectedState;
                },
                enumerable: true,
            },
            postalCode: {
                get: () => postalCode,
                set: newPostalCode => postalCode = nullSafeToString(newPostalCode),
                enumerable: true,
            },
        });

        this.firstName = nullSafeToString(data.firstName);
        this.lastName = nullSafeToString(data.lastName);
        this.company = nullSafeToString(data.company);
        this.phone = nullSafeToString(data.phone);
        this.address = nullSafeToString(data.address);
        this.address2 = nullSafeToString(data.address2);
        this.city = nullSafeToString(data.city);
        this.addressType = data.addressType || 'SHIP_TO';

        if (data.country) {
            this.country = data.country;
        }
    }
}

export default CheckoutAddress;
